import {
  Archive,
  Banknote,
  CreditCard,
  Landmark,
  type LucideIcon,
  Pencil,
  PiggyBank,
  TrendingUp,
  Wallet,
} from "lucide-react";
import { type FormEvent, useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, PageHeader } from "@/components/ui/card";
import { EmptyState, ErrorBanner, Loading } from "@/components/ui/feedback";
import { Select, TextInput } from "@/components/ui/field";
import { useAccounts, useArchiveAccount, useCreateAccount, useUpdateAccount } from "../../api/finance";
import { ACCOUNT_TYPE_LABEL, type Account, type AccountType } from "../../api/types";
import { Amount } from "../../components/Amount";
import { MoneyInput } from "../../components/MoneyInput";
import { useCan, useWorkspace } from "../../layout/workspace-context";
import { todayCivil } from "../../lib/dates";
import { parseMoneyInput } from "../../lib/money";

export function AccountsPage() {
  const ws = useWorkspace();
  const canEdit = useCan("catalog:write");
  const accounts = useAccounts(ws.id);
  const archive = useArchiveAccount(ws.id);

  return (
    <>
      <PageHeader title="Contas" />
      {accounts.isPending ? (
        <Loading />
      ) : accounts.isError ? (
        <ErrorBanner error={accounts.error} />
      ) : accounts.data.length === 0 ? (
        <EmptyState title="Nenhuma conta ainda." />
      ) : (
        <ul aria-label="Contas" className="m-0 mb-8 grid list-none gap-4 p-0 sm:grid-cols-2 xl:grid-cols-3">
          {accounts.data.map((a) => (
            <AccountRow key={a.id} account={a} canEdit={canEdit} onArchive={() => archive.mutate(a.id)} />
          ))}
        </ul>
      )}
      <ErrorBanner error={archive.error} />
      {canEdit && <NewAccountForm />}
    </>
  );
}

function AccountRow({
  account,
  canEdit,
  onArchive,
}: {
  account: Account;
  canEdit: boolean;
  onArchive: () => void;
}) {
  const ws = useWorkspace();
  const update = useUpdateAccount(ws.id);
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(account.name);

  async function save(e: FormEvent) {
    e.preventDefault();
    await update.mutateAsync({ id: account.id, name: name.trim() });
    setEditing(false);
  }

  const Icon = TYPE_ICON[account.type];
  return (
    <li className="flex min-w-0 flex-col gap-4 rounded-card bg-surface p-5 shadow-card">
      <div className="flex items-start gap-3">
        <span
          aria-hidden="true"
          className="flex size-10 shrink-0 items-center justify-center rounded-control bg-accent-soft text-accent-soft-ink"
        >
          <Icon className="size-5" />
        </span>
        <div className="min-w-0 flex-1">
          {editing ? (
            <form onSubmit={(e) => void save(e).catch(() => undefined)}>
              <label className="sr-only" htmlFor={`rename-${account.id}`}>
                Novo nome de {account.name}
              </label>
              <input
                id={`rename-${account.id}`}
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="min-h-9 w-full rounded-chip border border-border bg-surface px-2 text-[15px] text-ink focus-visible:border-accent focus-visible:outline-2 focus-visible:outline-accent/30"
              />
            </form>
          ) : (
            <p className="m-0 truncate font-semibold">{account.name}</p>
          )}
          <p className="m-0 text-xs text-muted">{ACCOUNT_TYPE_LABEL[account.type]}</p>
        </div>
        {canEdit && !editing && (
          <div className="-mr-2 -mt-2 flex shrink-0">
            <Button
              variant="ghost"
              size="icon"
              aria-label={`Renomear ${account.name}`}
              title="Renomear"
              onClick={() => setEditing(true)}
            >
              <Pencil aria-hidden="true" className="size-4" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              aria-label={`Arquivar ${account.name}`}
              title="Arquivar"
              onClick={onArchive}
            >
              <Archive aria-hidden="true" className="size-4" />
            </Button>
          </div>
        )}
      </div>
      <p className="m-0 text-2xl font-semibold tracking-[-0.02em]">
        <span className="sr-only">Saldo: </span>
        <Amount minor={account.balanceMinor} currency={account.currency} />
      </p>
    </li>
  );
}

const TYPE_ICON: Record<AccountType, LucideIcon> = {
  checking: Landmark,
  savings: PiggyBank,
  credit_card: CreditCard,
  cash: Banknote,
  investment: TrendingUp,
  other: Wallet,
};

function NewAccountForm() {
  const ws = useWorkspace();
  const create = useCreateAccount(ws.id);
  const [name, setName] = useState("");
  const [type, setType] = useState<AccountType>("checking");
  const [opening, setOpening] = useState("");
  const [negative, setNegative] = useState(false);
  const [invalid, setInvalid] = useState(false);

  async function submit(e: FormEvent) {
    e.preventDefault();
    const trimmed = opening.trim();
    const parsed = trimmed === "" || /^0+([,.]0+)?$/.test(trimmed) ? 0 : parseMoneyInput(trimmed);
    if (parsed === null) {
      setInvalid(true);
      return;
    }
    setInvalid(false);
    await create.mutateAsync({
      name: name.trim(),
      type,
      openingBalanceMinor: negative ? -parsed : parsed,
      openingDate: todayCivil(ws.timezone),
    });
    setName("");
    setOpening("");
    setNegative(false);
  }

  return (
    <Card title="Nova conta" className="max-w-xl">
      <form onSubmit={(e) => void submit(e).catch(() => undefined)} className="flex flex-col gap-4">
        <TextInput
          label="Nome"
          required
          maxLength={80}
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
        <Select label="Tipo" value={type} onChange={(e) => setType(e.target.value as AccountType)}>
          {Object.entries(ACCOUNT_TYPE_LABEL).map(([value, label]) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
        </Select>
        <MoneyInput label="Saldo inicial" value={opening} onChange={(e) => setOpening(e.target.value)} />
        {invalid && <p className="m-0 text-sm text-expense">Informe um valor válido, ex.: 1.234,56</p>}
        <label className="flex min-h-11 items-center gap-2 text-sm">
          <input
            type="checkbox"
            className="size-4 accent-accent"
            checked={negative}
            onChange={(e) => setNegative(e.target.checked)}
          />
          Saldo negativo (dívida)
        </label>
        <ErrorBanner error={create.error} />
        <Button type="submit" disabled={create.isPending}>
          Criar conta
        </Button>
      </form>
    </Card>
  );
}
